import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useStream } from "@langchain/langgraph-sdk/react";
import { Client } from "@langchain/langgraph-sdk";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { useOrganization } from '../../../context/OrganizationContext';
import { useUser } from '../../../context/UserContext';
import AssistantIcon from '@mui/icons-material/Assistant';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ShareIcon from '@mui/icons-material/Share';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import apisConfig from '../../../config/apis.json';

function columnToLetter(column) {
  let temp, letter = '', index = column + 1;
  while (index > 0) {
    temp = (index - 1) % 26;
    letter = String.fromCharCode(temp + 65) + letter;
    index = (index - temp - 1) / 26;
  }
  return letter;
}

function formatRange(range) {
  const { fromRow, toRow, fromCol, toCol } = range;
  return `${columnToLetter(fromCol)}${fromRow + 1}:${columnToLetter(toCol)}${toRow + 1}`;
}

function extractContentParts(content) {
  // Claude extended thinking returns content as an array of blocks:
  //   [{ type: "thinking", thinking: "..." }, { type: "text", text: "..." }]
  // LangChain may normalize thinking → reasoning:
  //   [{ type: "reasoning", reasoning: "..." }, { type: "text", text: "..." }]
  if (!Array.isArray(content)) {
    return { thinking: null, text: typeof content === "string" ? content : JSON.stringify(content, null, 2) };
  }

  let thinking = "";
  let text = "";
  for (const block of content) {
    if (block.type === "thinking" || block.type === "reasoning") {
      thinking += block.thinking || block.reasoning || "";
    } else if (block.type === "text") {
      text += block.text || "";
    }
  }
  return { thinking: thinking || null, text: text || "" };
}

// ---------------------------------------------------------------------------
// Thinking Section — inspired by ChatGPT / Claude / Cursor patterns
// ---------------------------------------------------------------------------
// During streaming: expanded with animated indicator and live thinking text
// After completion: collapses to a single-line summary, click to expand
// Uses a left-border accent and sparkle icon, smooth framer-motion transitions
// ---------------------------------------------------------------------------

function ThinkingSection({ thinking, isActivelyThinking }) {
  const [expanded, setExpanded] = useState(isActivelyThinking);
  const [elapsedSec, setElapsedSec] = useState(0);
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);

  // Auto-expand when actively thinking, auto-collapse when done
  useEffect(() => {
    if (isActivelyThinking) {
      setExpanded(true);
      if (!startTimeRef.current) startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setElapsedSec(Math.round((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      // Auto-collapse after a short delay when thinking finishes
      const collapseTimer = setTimeout(() => setExpanded(false), 600);
      return () => clearTimeout(collapseTimer);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isActivelyThinking]);

  if (!thinking && !isActivelyThinking) return null;

  const previewText = thinking
    ? thinking.split('\n').filter(Boolean)[0]?.substring(0, 100)
    : '';

  return (
    <div className="mb-3">
      {/* Toggle header */}
      <button
        onClick={() => setExpanded(prev => !prev)}
        className="group flex flex-col items-start gap-0.5 w-full text-left transition-colors"
      >
        <div className="flex items-center gap-1.5">
          <AutoAwesomeIcon
            style={{ fontSize: '14px' }}
            className={`text-[#404880] ${isActivelyThinking ? 'animate-pulse' : ''}`}
          />
          <span className="text-xs font-medium text-[#404880]/80">
            {isActivelyThinking
              ? `Thinking${elapsedSec > 0 ? ` for ${elapsedSec}s` : ''}...`
              : `Thought for ${elapsedSec || '< 1'}s`
            }
          </span>
          <ExpandMoreIcon
            style={{ fontSize: '16px' }}
            className={`text-gray-400 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`}
          />
        </div>
        {/* Preview text when collapsed */}
        {!expanded && previewText && (
          <span className="text-[11px] text-gray-400 truncate max-w-full ml-5">
            {previewText}...
          </span>
        )}
      </button>

      {/* Expandable thinking content */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div
              className={`
                mt-2 ml-[7px] border-l-2 border-[#404880]/30 pl-3 pr-1
                text-[12px] leading-relaxed text-gray-500
                max-h-52 overflow-y-auto scrollbar-thin
              `}
            >
              {thinking ? (
                <div className="whitespace-pre-wrap">{thinking}</div>
              ) : (
                <div className="flex items-center gap-1.5 py-1 text-gray-400">
                  <span className="inline-block h-1 w-1 rounded-full bg-[#404880] animate-bounce [animation-delay:0ms]" />
                  <span className="inline-block h-1 w-1 rounded-full bg-[#404880] animate-bounce [animation-delay:150ms]" />
                  <span className="inline-block h-1 w-1 rounded-full bg-[#404880] animate-bounce [animation-delay:300ms]" />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MarkdownBody({ content, isStreaming }) {
  const { thinking, text } = extractContentParts(content);
  // Determine if this message is actively in the thinking phase (has thinking, still streaming, no text yet)
  const isActivelyThinking = isStreaming && !!thinking && !text;

  return (
    <>
      <ThinkingSection thinking={thinking} isActivelyThinking={isActivelyThinking} />
      <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeHighlight]}
      components={{
        h1: ({ children }) => (
          <h1 className="text-base font-semibold text-gray-900 mt-4 mb-2 first:mt-0">{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-[0.9rem] font-semibold text-gray-900 mt-3 mb-1.5 first:mt-0">{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-sm font-medium text-gray-800 mt-2.5 mb-1 first:mt-0">{children}</h3>
        ),
        h4: ({ children }) => (
          <h4 className="text-sm font-medium text-gray-700 mt-2 mb-1 first:mt-0">{children}</h4>
        ),
        p: ({ children }) => (
          <p className="text-sm text-gray-700 leading-relaxed mb-2 last:mb-0">{children}</p>
        ),
        a: ({ node, ...props }) => (
          <a className="underline decoration-dotted underline-offset-2 hover:text-blue-600" {...props} />
        ),
        code: ({ inline, className, children, ...props }) => inline ? (
          <code className="rounded bg-gray-100 px-1 py-0.5 text-[0.85em]" {...props}>{children}</code>
        ) : (
          <pre className="my-2 overflow-auto rounded-lg border bg-gray-50 p-2 text-sm">
            <code className={className} {...props}>{children}</code>
          </pre>
        ),
        table: ({ children }) => (
          <div className="not-prose my-2 overflow-hidden rounded-lg border">
            <table className="w-full text-sm">{children}</table>
          </div>
        ),
        th: ({ children }) => (
          <th className="bg-gray-100 px-2 py-1 text-left text-xs font-medium">{children}</th>
        ),
        td: ({ children }) => (
          <td className="px-2 py-1 text-sm text-gray-600">{children}</td>
        ),
        ol: ({ children, start, ...props }) => (
          <ol className="list-decimal list-outside pl-6 space-y-1 my-2 text-sm" start={start} {...props}>{children}</ol>
        ),
        ul: ({ children, ...props }) => (
          <ul className="list-disc list-outside pl-6 space-y-1 my-2 text-sm" {...props}>{children}</ul>
        ),
        li: ({ children, ...props }) => (
          <li className="pl-1 text-gray-700" {...props}>{children}</li>
        ),
        strong: ({ children }) => (
          <strong className="font-semibold text-gray-900">{children}</strong>
        ),
        hr: () => <hr className="my-3 border-gray-200" />,
        blockquote: ({ children }) => (
          <blockquote className="border-l-2 border-gray-300 pl-3 my-2 text-sm text-gray-600 italic">{children}</blockquote>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
    </>
  );
}

// ---------------------------------------------------------------------------
// Group consecutive messages by type (human / ai) for stepper rendering
// ---------------------------------------------------------------------------
function groupMessages(messages) {
  const out = [];
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    const prev = out[out.length - 1];
    if (prev && prev.type === m.type) prev.items.push(m);
    else out.push({ type: m.type, items: [m] });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Vertical stepper for AI message groups — shows per-node progress
// ---------------------------------------------------------------------------
function AiVerticalStepper({ items, currentNode, isLoading }) {
  const nodeLabel = (node) =>
    node === 'reason' ? 'Reasoning about the data...'
    : node === 'generate_code' ? 'Generating code...'
    : node === 'execute' ? 'Executing in sandbox...'
    : node === 'complete' ? 'Preparing summary...'
    : node === 'supervisor' ? 'Deciding approach...'
    : node === 'initialize' ? 'Loading context...'
    : node === 'analyze' ? 'Analyzing...'
    : 'Processing...';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.18 }}
      className="w-full rounded-xl ring-1 ring-gray-200 bg-white/80 backdrop-blur-sm shadow-sm"
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-3 py-1.5">
        <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
          <AssistantIcon style={{ fontSize: '14px' }} className="text-[#404880]" />
          Analyst
        </div>
      </div>

      <ol className="relative ml-5 py-2">
        <span className="absolute left-0 top-0 h-full w-px bg-gray-200" />

        {items.map((m, idx) => {
          const isLastStep = idx === items.length - 1;
          const isActive = isLastStep && isLoading && currentNode;
          const isStreamingMsg = isLastStep && isLoading;

          return (
            <li key={m.id} className="relative pl-5 pr-3 py-2">
              {/* Dot */}
              <span
                className={`absolute left-[-5px] top-3 inline-flex rounded-full ${
                  isActive
                    ? 'h-2.5 w-2.5 bg-brand-pink ring-4 ring-brand-pink/10'
                    : isLastStep
                    ? 'h-2.5 w-2.5 bg-gray-600 ring-4 ring-gray-100'
                    : 'h-2 w-2 bg-gray-400'
                }`}
              />
              {/* Timestamp */}
              <div className="mb-1 text-[11px] text-gray-400">
                {new Date(m.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
              {/* Content */}
              <div className="max-w-none min-w-0">
                <MarkdownBody content={m.content} isStreaming={isStreamingMsg} />
              </div>

              {/* Typing indicator below active step */}
              {isActive && (
                <div className="flex items-center gap-2 py-2 mt-2 border-t border-gray-100">
                  <div className="flex gap-1">
                    <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-brand-pink [animation-delay:0ms]" />
                    <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-brand-pink [animation-delay:100ms]" />
                    <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-brand-pink [animation-delay:200ms]" />
                  </div>
                  <span className="text-xs text-gray-500">{nodeLabel(currentNode)}</span>
                </div>
              )}
            </li>
          );
        })}

        {/* Empty loading state — no items yet but agent is working */}
        {items.length === 0 && isLoading && currentNode && (
          <li className="relative pl-5 pr-3 py-2">
            <span className="absolute left-[-5px] top-3 inline-flex h-2.5 w-2.5 rounded-full bg-brand-pink ring-4 ring-brand-pink/10" />
            <div className="mb-1 text-[11px] text-gray-400">
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className="flex items-center gap-2 py-1">
              <div className="flex gap-1">
                <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-brand-pink [animation-delay:0ms]" />
                <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-brand-pink [animation-delay:100ms]" />
                <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-brand-pink [animation-delay:200ms]" />
              </div>
              <span className="text-xs text-gray-500">{nodeLabel(currentNode)}</span>
            </div>
          </li>
        )}
      </ol>
    </motion.div>
  );
}

const AnalystChat = ({ conversation, onScrollToCell, threadId, isStreaming, setIsStreaming }) => {
  const { orgId } = useOrganization();
  const { userInfo } = useUser();
  const chatEndRef = useRef(null);
  const [currentNode, setCurrentNode] = useState(null);
  const [error, setError] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const activeRunIdRef = useRef(null);
  const eventLogRef = useRef([]);

  // Create LangGraph Client
  const client = useMemo(() => {
    const apiUrl = process.env.REACT_APP_ENVIRON === 'development'
      ? apisConfig.dev
      : apisConfig.prod;

    return new Client({
      apiUrl: `${apiUrl}/api/langgraph`,
      onRequest: (url, init) => {
        const headers = {
          ...init.headers,
          'x-organization-id': orgId,
          'x-thread-id': threadId || '',
          'x-user-id': userInfo?._id || '',
          'x-assistant-id': 'analyst'
        };
        return {
          ...init,
          headers,
          credentials: 'include'
        };
      }
    });
  }, [orgId, threadId, userInfo?._id]);

  const handleReconnection = useCallback((err) => {
    if (reconnectAttempts >= 3) {
      setError('Failed to reconnect. Please refresh the page.');
      setIsReconnecting(false);
      return;
    }
    setIsReconnecting(true);
    setReconnectAttempts(prev => prev + 1);
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    const backoffDelay = Math.pow(2, reconnectAttempts) * 1000;
    reconnectTimeoutRef.current = setTimeout(() => setIsReconnecting(false), backoffDelay);
  }, [reconnectAttempts]);

  const threadStream = useStream({
    client,
    assistantId: "analyst",
    threadId: threadId || undefined,
    reconnectOnMount: true,
    fetchStateHistory: true,

    onMetadataEvent: (evt) => {
      eventLogRef.current.push({ type: 'metadata', event: evt, timestamp: new Date() });
      if (!activeRunIdRef.current && evt?.run_id) {
        activeRunIdRef.current = evt.run_id;
        if (threadId) sessionStorage.setItem(`analyst_run_${threadId}`, evt.run_id);
      }
    },

    onLangChainEvent: (evt) => {
      const nodeName = evt?.metadata?.langgraph_node || evt?.name || "unknown";
      if (evt.event === "on_chain_start") setCurrentNode(nodeName);
    },

    onUpdateEvent: (data) => {
      eventLogRef.current.push({ type: 'update', event: data, timestamp: new Date() });
      // Track node transitions from state updates (more reliable for subgraph nodes)
      if (data && typeof data === 'object') {
        const nodeNames = Object.keys(data);
        if (nodeNames.length > 0) {
          const latestNode = nodeNames[nodeNames.length - 1];
          // Only update if it looks like a node name (not a state key)
          if (['reason', 'generate_code', 'execute', 'complete', 'supervisor', 'initialize', 'analyze', 'aggregate'].includes(latestNode)) {
            setCurrentNode(latestNode);
          }
        }
      }
    },

    onFinish: () => {
      setCurrentNode(null);
      setIsStreaming(false);
      setIsReconnecting(false);
      setReconnectAttempts(0);
      if (threadId) sessionStorage.removeItem(`analyst_run_${threadId}`);
      activeRunIdRef.current = null;
    },

    onError: (err) => {
      const isFatal = err?.message?.includes('404') || err?.message?.includes('Thread not found');
      if (isFatal) {
        setError('Thread not found or has been deleted.');
        setIsReconnecting(false);
      } else {
        handleReconnection(err);
      }
      setCurrentNode(null);
      setIsStreaming(false);
      activeRunIdRef.current = null;
    },
  });

  // Expose submit function for parent
  const submitToAgent = useCallback((jsonData, selectedRange, menu, submenu, prompt, overrideThreadId) => {
    if (!threadStream) return;

    setError(null);
    setIsStreaming(true);

    const content = JSON.stringify({
      data: jsonData,
      range: selectedRange,
      menu,
      submenu,
      prompt: prompt || null,
    });

    const effectiveThreadId = overrideThreadId || threadId;

    threadStream.submit(
      { messages: [{ type: "human", content }] },
      {
        streamMode: ["messages", "updates"],
        streamSubgraphs: true,
        threadId: effectiveThreadId || undefined,
        ifNotExists: "create",
      }
    );
  }, [threadStream, threadId, setIsStreaming]);

  // Make submitToAgent accessible to parent
  React.useImperativeHandle(
    React.useRef(null),
    () => ({ submitToAgent }),
    [submitToAgent]
  );

  // Attach submitToAgent to the component so the parent can call it
  AnalystChat.submitRef = submitToAgent;

  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation, threadStream?.messages]);

  // Compute messages from LangGraph stream
  const streamMessages = useMemo(() => {
    return (threadStream?.messages || []).filter(m => m.type === "human" || m.type === "ai");
  }, [threadStream?.messages]);

  const handleFullscreen = (content) => {
    sessionStorage.setItem('analyst-fullscreen', typeof content === 'string' ? content : JSON.stringify(content));
    const orgPath = window.location.pathname.match(/\/organization\/[^/]+/)?.[0] || '';
    window.open(`${orgPath}/analyst/fullscreen`, '_blank');
  };

  // Determine what to show: LangGraph stream messages if available, otherwise local conversation
  const displayMessages = streamMessages.length > 0 ? streamMessages : [];
  const grouped = useMemo(() => groupMessages(displayMessages), [displayMessages]);

  return (
    <div className="flex flex-col h-full">
      {error && (
        <div className="mx-3 mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-3 scrollbar-thin">
        {/* NOTE: Local conversation messages only render when no LangGraph stream messages exist yet.
            Once threadStream.messages arrives, it contains the full ordered history (human+AI pairs),
            so we switch entirely to that to avoid duplicates and broken ordering.
            If we later want to show local-only UI (e.g. range highlights, scroll-to-cell buttons)
            alongside stream messages, we'll need to merge the two sources by matching on message content
            or add metadata to the conversation items to correlate them with stream messages. */}
        {displayMessages.length === 0 && conversation.map((item, index) => (
          <React.Fragment key={`local-${index}`}>
            {item.role === 'user' && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="ml-auto max-w-[85%] relative"
              >
                <div className="bg-[#D3E3FD]/40 rounded-xl px-3 py-2 text-sm">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                    <span className="font-medium text-gray-700">{item.menu}</span>
                    {item.submenu && (
                      <>
                        <span>/</span>
                        <span className="font-medium text-gray-700">{item.submenu}</span>
                      </>
                    )}
                  </div>
                  <p className="text-gray-600 text-xs">Range: {item.content}</p>
                  {item.range && (
                    <button
                      onClick={() => onScrollToCell(item.range)}
                      type="button"
                      className="mt-1.5 text-[10px] bg-brand-pink text-white rounded px-2 py-0.5 hover:bg-brand-pink/80 transition-colors"
                    >
                      show on spreadsheet
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </React.Fragment>
        ))}

        {/* LangGraph streamed messages — grouped with stepper for AI */}
        {grouped.map((group, gi) => (
          <React.Fragment key={`grp-${gi}`}>
            {group.type === 'human' && group.items.map((m) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="ml-auto max-w-[85%]"
              >
                <div className="bg-[#D3E3FD]/40 rounded-xl px-3 py-2 text-sm text-gray-900">
                  <p className="text-xs text-gray-600 break-words">{(() => {
                    try {
                      const parsed = JSON.parse(m.content);
                      return `${parsed.menu || 'Analysis'}${parsed.submenu ? ' / ' + parsed.submenu : ''}${parsed.prompt ? ': ' + parsed.prompt : ''}`;
                    } catch {
                      return m.content?.length > 200 ? m.content.substring(0, 200) + '...' : m.content;
                    }
                  })()}</p>
                </div>
              </motion.div>
            ))}

            {group.type === 'ai' && (
              group.items.length > 1 || (isStreaming && currentNode && gi === grouped.length - 1)
            ) ? (
              <AiVerticalStepper
                items={group.items}
                currentNode={currentNode}
                isLoading={isStreaming}
              />
            ) : group.type === 'ai' && (
              group.items.map((m, idx) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="max-w-full relative"
                >
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 ring-1 ring-gray-200 shadow-sm">
                    <div className="max-w-none min-w-0">
                      <MarkdownBody
                        content={m.content}
                        isStreaming={isStreaming && gi === grouped.length - 1 && idx === group.items.length - 1}
                      />
                    </div>
                    {!(isStreaming && gi === grouped.length - 1 && idx === group.items.length - 1) && (
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => handleFullscreen(m.content)}
                          className="text-[10px] bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-2 py-0.5 flex items-center gap-1 transition-colors"
                        >
                          <FullscreenIcon style={{ fontSize: '12px' }} /> Expand
                        </button>
                        <button
                          className="text-[10px] bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-2 py-0.5 flex items-center gap-1 transition-colors"
                        >
                          <ShareIcon style={{ fontSize: '12px' }} /> Share
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </React.Fragment>
        ))}

        {/* Empty stepper when waiting for AI response (last group is human and still streaming) */}
        {isStreaming && currentNode && grouped.length > 0 && grouped[grouped.length - 1].type === 'human' && (
          <AiVerticalStepper items={[]} currentNode={currentNode} isLoading={true} />
        )}

        {/* Streaming / typing indicator — only when no grouped stepper is showing */}
        {isStreaming && !currentNode && (
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="flex gap-1">
              <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-brand-pink [animation-delay:0ms]" />
              <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-brand-pink [animation-delay:100ms]" />
              <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-brand-pink [animation-delay:200ms]" />
            </div>
            <span className="text-xs text-gray-500">Processing...</span>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>
    </div>
  );
};

export default AnalystChat;
